import * as Sentry from "@sentry/react";
import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { useMutation } from "@apollo/client";
import _debounce from "lodash/debounce";
import {
  selectCheckedGlobalTypeWells,
  selectSelectedTypeWell,
  useForecastStore
} from "stores/forecast";

import { UPDATE_TYPE_WELL_STYLE } from "api/userArps";

import { UserArpsItem } from "models/UserArpsModel";

import { ColorEditor } from "components/shared";

interface TypeWellColorEditorProps {
  forecastTwNode: UserArpsItem;
  isDisabled: boolean;
  memoizedUpdateTypeWellDataWithParentNode: (node: Partial<UserArpsItem>) => void;
}

export default function TypeWellColorEditor({
  forecastTwNode,
  isDisabled,
  memoizedUpdateTypeWellDataWithParentNode
}: TypeWellColorEditorProps) {
  const selectedTypeWell = useForecastStore(selectSelectedTypeWell);
  const checkedGlobalTypeWells = useForecastStore(selectCheckedGlobalTypeWells);
  const fsSetCheckedGlobalTypeWells = useForecastStore((s) => s.setCheckedGlobalTypeWells);
  const fsSetSelectedTypeWell = useForecastStore((s) => s.setSelectedTypeWell);
  const [updateTypeWellStyle] = useMutation(UPDATE_TYPE_WELL_STYLE);

  const latestStyleRef = useRef({
    color: forecastTwNode.color,
    thickness: forecastTwNode.thickness
  });
  const lastSavedStyleRef = useRef({
    color: forecastTwNode.color,
    thickness: forecastTwNode.thickness
  });

  useEffect(() => {
    latestStyleRef.current = {
      color: forecastTwNode.color,
      thickness: forecastTwNode.thickness
    };
  }, [forecastTwNode.color, forecastTwNode.thickness]);

  useEffect(() => {
    lastSavedStyleRef.current = {
      color: forecastTwNode.color,
      thickness: forecastTwNode.thickness
    };
  }, [forecastTwNode.id]);

  // Use a ref to store the latest checkedGlobalTypeWells for the debounced function
  const latestCheckedTypeWellsRef = useRef(checkedGlobalTypeWells);
  latestCheckedTypeWellsRef.current = checkedGlobalTypeWells;

  // Update checked global type wells with the latest style for the target id
  const updateReduxWithLatestColors = useCallback(
    (targetId: string, color: string, thickness: number) => {
      const currentCheckedTypeWells = latestCheckedTypeWellsRef.current;
      const checkedCopy = [...currentCheckedTypeWells];

      for (let j = 0; j < checkedCopy.length; j++) {
        const tw = checkedCopy[j];
        if ((tw?.children ?? []).length > 0) {
          const childrenCopy = [...tw.children];
          for (let i = 0; i < childrenCopy.length; i++) {
            const child = childrenCopy[i];
            if (child.id === targetId) {
              childrenCopy[i] = { ...child, color, thickness };
            }
          }
          checkedCopy[j] = { ...tw, children: childrenCopy };
        } else if (tw.id === targetId) {
          checkedCopy[j] = { ...tw, color, thickness };
        }
      }
      fsSetCheckedGlobalTypeWells(checkedCopy);
    },
    [fsSetCheckedGlobalTypeWells]
  );

  // Debounced version for thickness changes
  const debouncedReduxUpdate = useMemo(
    () => _debounce(updateReduxWithLatestColors, 300), // 300ms debounce for thickness slider
    [updateReduxWithLatestColors]
  );

  const updateTypeWellColor = useCallback(
    (color: string, thickness: number) => {
      const targetId = forecastTwNode.id;
      if (!targetId) return;

      const currentColor = latestStyleRef.current.color;
      const currentThickness = latestStyleRef.current.thickness;

      // Update the tree data immediately
      const cloneNode = { ...forecastTwNode };
      cloneNode.color = color;
      cloneNode.thickness = thickness;
      memoizedUpdateTypeWellDataWithParentNode(cloneNode);

      latestStyleRef.current = { color, thickness };

      const isColorChange = currentColor !== color;
      const isThicknessChange = currentThickness !== thickness;

      // For color changes: update Redux immediately
      // For thickness changes: use debounced update
      if (isColorChange) {
        updateReduxWithLatestColors(targetId, color, thickness);
      } else if (isThicknessChange) {
        debouncedReduxUpdate(targetId, color, thickness);
      }

      // Check if the type well is the selected one and update its colors
      if (targetId === selectedTypeWell?.id) {
        const updatedSelectedTypeWell = { ...selectedTypeWell, color, thickness };
        fsSetSelectedTypeWell(updatedSelectedTypeWell);
      }
    },
    [
      forecastTwNode,
      selectedTypeWell,
      fsSetSelectedTypeWell,
      memoizedUpdateTypeWellDataWithParentNode,
      updateReduxWithLatestColors,
      debouncedReduxUpdate
    ]
  );

  const handleStylePersist = useCallback(async () => {
    const targetId = forecastTwNode.id;
    if (!targetId) return;

    const { color, thickness } = latestStyleRef.current;
    const lastSaved = lastSavedStyleRef.current;

    if (color === lastSaved.color && thickness === lastSaved.thickness) {
      return;
    }

    try {
      await updateTypeWellStyle({
        variables: {
          input: {
            forecastId: targetId,
            color,
            thickness
          }
        }
      });
      lastSavedStyleRef.current = { color, thickness };
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [forecastTwNode.id, updateTypeWellStyle]);

  return (
    <>
      <ColorEditor
        data-testid="forecast-node-color-edit-button"
        isDisabled={isDisabled}
        onChange={updateTypeWellColor}
        onClose={() => {
          void handleStylePersist();
        }}
        color={forecastTwNode.color}
        thickness={forecastTwNode.thickness}
        squareDisplay={true}
        disableTransparent={true}
      />
    </>
  );
}
